import { Dayjs } from 'dayjs';
import { mixed, object, ObjectSchema } from 'yup';

import { BookingModeDto, DictionaryTypeDto } from '@/api/generated';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import {
  ProcessCalendarEntryQueryFormModel,
  ProcessCalendarEntryQueryValidationContext,
} from '@/views/processes/calendarEntryQuery/model/ProcessCalendarEntryQueryForm.model.ts';

type Messages = {
  dictionaryTypeRequired: string;
  rangeIncomplete: string;
  rangeOrder: string;
};

const isRangeComplete = (value: Dayjs | null | undefined, other: Dayjs | null | undefined) =>
  !value === !other;

/**
 * Rules of the calendar entry query: an entry needs its dictionary type, the range has both bounds
 * or none, and the range goes forward (whole days may start and end on the same day).
 */
export const processCalendarEntryQueryShape = (messages: Messages) => ({
  requestedDictionaryId: mixed<SelectSearchOptionModel<number>>()
    .nullable()
    .default(null)
    .test('requires-type', messages.dictionaryTypeRequired, (value, context) => {
      const parent = context.parent as ProcessCalendarEntryQueryFormModel;
      return !value || Boolean(parent.requestedDictionaryType);
    }),
  requestedDictionaryType: mixed<SelectSearchOptionModel<DictionaryTypeDto>>()
    .nullable()
    .default(null),
  requestedFrom: mixed<Dayjs>()
    .nullable()
    .default(null)
    .test('range-complete', messages.rangeIncomplete, (value, context) =>
      isRangeComplete(value, (context.parent as ProcessCalendarEntryQueryFormModel).requestedTo),
    ),
  requestedTo: mixed<Dayjs>()
    .nullable()
    .default(null)
    .test('range-complete', messages.rangeIncomplete, (value, context) =>
      isRangeComplete(value, (context.parent as ProcessCalendarEntryQueryFormModel).requestedFrom),
    )
    .test('range-order', messages.rangeOrder, (value, context) => {
      const from = (context.parent as ProcessCalendarEntryQueryFormModel).requestedFrom;
      if (!value || !from) return true;
      const { bookingMode } = (context.options.context ??
        {}) as ProcessCalendarEntryQueryValidationContext;
      return bookingMode === BookingModeDto.ExactTime
        ? from.isBefore(value)
        : !value.isBefore(from, 'day');
    }),
});

export const useProcessCalendarEntryQueryMessages = (): Messages => {
  const { t } = useTranslationWithPrefix('validation');
  const { t: formT } = useTranslationWithPrefix('views.processes.calendarEntryQuery');
  return {
    dictionaryTypeRequired: formT('dictionaryTypeRequired'),
    rangeIncomplete: formT('rangeIncomplete'),
    rangeOrder: t('date.rangeOrder'),
  };
};

export const useProcessCalendarEntryQueryValidation =
  (): ObjectSchema<ProcessCalendarEntryQueryFormModel> =>
    object(processCalendarEntryQueryShape(useProcessCalendarEntryQueryMessages()));
